import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, WhatsappLogo } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import ShortsGrid from "@/components/ShortsGrid";
import { Reveal, EASE } from "@/components/motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  PROJECTS,
  PROJECT_CATEGORIES,
  IMG,
  whatsappLink,
} from "@/lib/data";

const Projects = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const filtered = useMemo(
    () => (filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <>
      <PageHeader
        overline="Projects · Selected Work"
        title={
          <>
            A portfolio of <br />
            <span className="italic">clarity and craft.</span>
          </>
        }
        description="A cross-section of residential and commercial projects — from private homes and showers to storefronts, offices and aluminium systems across Ahmedabad."
        testid="projects-header"
        image={IMG.heroFacade}
      />

      {/* Filter bar */}
      <div className="sticky top-[76px] z-30 border-y border-black/10 bg-offwhite/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] gap-2 overflow-x-auto px-5 py-3 md:gap-3 md:px-8 md:py-4">
          {PROJECT_CATEGORIES.map((c) => {
            const isActive = filter === c;
            return (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`shrink-0 rounded-sm border px-4 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  isActive
                    ? "border-ink bg-ink text-offwhite"
                    : "border-black/15 text-muted-foreground hover:border-ink hover:text-ink"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Gallery */}
      <section className="mx-auto max-w-[1400px] px-5 py-16 md:px-8 md:py-24">
        <motion.div layout className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, ease: EASE, delay: (i % 6) * 0.03 }}
                onClick={() => setActive(p)}
                data-testid={`project-${p.id}`}
                className="group block text-left"
              >
                <div className="overflow-hidden rounded-sm border border-black/10 bg-white">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                  </div>
                  <div className="flex items-start justify-between gap-3 p-5">
                    <div>
                      <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-gold">{p.category} · {p.location}</span>
                      <h3 className="mt-2 font-display text-lg font-medium tracking-tight text-ink">{p.title}</h3>
                    </div>
                    <ArrowUpRight size={18} className="mt-1 shrink-0 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p className="py-16 text-center text-sm text-muted-foreground">
            No projects in this category yet.
          </p>
        )}
      </section>

      {/* Reels — YouTube Shorts */}
      <section className="border-t border-black/10 bg-cream/40 py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <span className="eyebrow rule">Reels · From the workshop</span>
              <h2 className="mt-5 font-display text-3xl font-medium tracking-tight text-ink md:text-5xl">
                In <span className="italic">motion.</span>
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Short vertical reels of installations, fabrication detail and finished work.
              </p>
            </div>
            <span className="text-xs text-muted-foreground">Tap a reel to play</span>
          </div>

          <div className="mt-12">
            <ShortsGrid />
          </div>
        </div>
      </section>

      {/* Project lightbox */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-5xl overflow-hidden border-black/10 bg-offwhite p-0" data-testid="project-lightbox">
          {active && (
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-3">
                <img src={active.img} alt={active.title} className="h-full max-h-[70vh] w-full object-cover" />
              </div>
              <div className="flex flex-col p-8 md:col-span-2 md:p-10">
                <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-gold">
                  {active.category} · {active.location}
                </span>
                <h3 className="mt-4 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">
                  {active.title}
                </h3>
                <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{active.desc}</p>
                <a
                  href={whatsappLink(`Hi Nagar Glass, I'd like to discuss a project similar to "${active.title}".`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lightbox-enquire"
                  className="mt-auto inline-flex w-fit items-center gap-2 rounded-sm bg-ink px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-offwhite transition-colors hover:bg-gold hover:text-ink"
                >
                  <WhatsappLogo weight="fill" size={16} /> Discuss on WhatsApp
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reel dialog handled inside ShortsGrid */}

      <FinalCTA />
    </>
  );
};

export default Projects;
